import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { makeAxiosProgress, type UploadProgressFn } from "@/lib/upload-progress";
import type { ArticleRow } from "@/lib/types";
import type { Visibility } from "./useDocumentMutations";

export interface ArticleInput {
  title?: string;
  content?: string;
  category?: string;
  visibility?: Visibility;
  image?: File | null;
  /** Optional progress reporter; fires while the multipart body uploads. */
  onProgress?: UploadProgressFn;
}

function buildFormData(input: ArticleInput): FormData {
  const form = new FormData();
  if (input.title      != null) form.append("title",      input.title);
  if (input.content    != null) form.append("content",    input.content);
  if (input.category   != null) form.append("category",   input.category);
  if (input.visibility != null) form.append("visibility", input.visibility);
  if (input.image)              form.append("image",      input.image);
  return form;
}

/** POST /api/v1/articles/ — server derives status from visibility. */
export function useCreateArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ArticleInput) => {
      const { data } = await api.post<ArticleRow>("/articles/", buildFormData(input), {
        onUploadProgress: makeAxiosProgress(input.onProgress),
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["articles"] }),
  });
}

/** PATCH /api/v1/articles/<slug>/ — partial update, image optional. */
export function useUpdateArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ slug, input }: { slug: string; input: ArticleInput }) => {
      const { data } = await api.patch<ArticleRow>(`/articles/${slug}/`, buildFormData(input), {
        onUploadProgress: makeAxiosProgress(input.onProgress),
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["articles"] }),
  });
}

/** DELETE /api/v1/articles/<slug>/. */
export function useDeleteArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slug: string) => { await api.delete(`/articles/${slug}/`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["articles"] }),
  });
}
